import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dateShort, dateTime, money, money0, pct, today } from '../lib/format';
import { PageHeader } from '../components/Layout';
import { InvoiceDoc, type Invoice } from '../components/InvoiceDoc';
import { RoomSetup } from '../components/RoomSetup';
import {
  ConfirmDialog,
  Drawer,
  Empty,
  ErrorState,
  Kpi,
  Loading,
  Modal,
  Select,
  StatusChip,
  TextInput,
  useToast,
} from '../components/ui';

interface Room {
  id: string;
  number: string;
  floor: number;
  status: 'VACANT' | 'OCCUPIED' | 'DIRTY' | 'OUT_OF_ORDER';
  roomTypeId: string;
  roomType: string;
  tariff: number;
  maxOccupancy: number;
  amenities: string[];
  gstRate: number;
  openTasks: number;
  stay: {
    id: string;
    folioNo: string;
    guestName: string;
    checkInAt: string;
    nights: number;
    pax: number;
  } | null;
}

interface RoomsSummary {
  total: number;
  occupied: number;
  vacant: number;
  dirty: number;
  outOfOrder: number;
  occupancyPct: number;
  adr: number;
  revpar: number;
}

interface FolioLine {
  description: string;
  hsn: string;
  quantity: number;
  unitPrice: number;
  gstRate: number;
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  total: number;
  sourceInvoiceNumber: string | null;
}

interface Folio {
  stay: {
    id: string;
    folioNo: string;
    status: string;
    roomNumber: string;
    roomType: string;
    guestName: string;
    phone: string | null;
    gstin: string | null;
    checkInAt: string;
    /** What was taken at the desk on arrival. */
    nights: number;
    /** What the folio is charging for, once the stay has run on. */
    nightsCharged: number;
    tariff: number;
    adults: number;
    children: number;
  };
  lines: FolioLine[];
  totals: {
    subtotal: number;
    cgst: number;
    sgst: number;
    igst: number;
    roundOff: number;
    total: number;
  };
  advance: number;
  balance: number;
}

interface OnlineBooking {
  id: string;
  reference: string;
  guestName: string;
  guestPhone: string;
  guestEmail: string | null;
  roomType: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  adults: number;
  children: number;
  total: number;
  tariff: number;
  status: string;
  paid: boolean;
  roomTypeId: string;
  stayId: string | null;
}

/**
 * What guests booked from the public page.
 *
 * Without this the front desk has no idea a room was sold: the booking exists,
 * the guest has a reference, and nobody behind the counter can see either.
 */
function OnlineBookings({ onCheckIn }: { onCheckIn: (booking: OnlineBooking) => void }) {
  const queryClient = useQueryClient();
  const { fail, push } = useToast();
  const { can } = useAuth();
  const [cancelling, setCancelling] = useState<OnlineBooking | null>(null);

  const bookings = useQuery({
    queryKey: ['bookings'],
    queryFn: () => api.get<OnlineBooking[]>('/api/bookings'),
    refetchInterval: 60_000,
  });

  const cancel = useMutation({
    mutationFn: (booking: OnlineBooking) => api.post(`/api/bookings/${booking.id}/cancel`, {}),
    onSuccess: (_result, booking) => {
      push(`${booking.reference} cancelled`, 'ok');
      setCancelling(null);
      void queryClient.invalidateQueries({ queryKey: ['bookings'] });
      void queryClient.invalidateQueries({ queryKey: ['rooms'] });
    },
    onError: fail,
  });

  const rows = bookings.data ?? [];
  const arriving = rows.filter((row) => row.checkIn === today() && row.status !== 'CANCELLED');

  /** Still coming: not cancelled, and not already at the desk. */
  const open = (row: OnlineBooking) => row.status === 'HELD' || row.status === 'CONFIRMED';

  return (
    <div className="card">
      <div className="card-head">
        <h2>Booked online</h2>
        <span className="small muted">
          {arriving.length > 0
            ? `${arriving.length} arriving today`
            : 'From your public booking link'}
        </span>
      </div>
      {bookings.isLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty message="Nobody has booked online yet. Share your booking link from the QR codes screen." />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Guest</th>
                <th>Room</th>
                <th>Stay</th>
                <th className="num">Value</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="mono xs">{row.reference}</td>
                  <td>
                    <div className="b">{row.guestName}</div>
                    <div className="xs muted">
                      {row.guestPhone}
                      {row.adults + row.children > 1
                        ? ` · ${row.adults + row.children} guests`
                        : ''}
                    </div>
                  </td>
                  <td className="small">{row.roomType}</td>
                  <td className="small">
                    {row.checkIn} to {row.checkOut}
                    <div className="xs muted">
                      {row.nights} night{row.nights === 1 ? '' : 's'}
                    </div>
                  </td>
                  <td className="num">{money0(row.total)}</td>
                  <td>
                    <StatusChip status={row.status} />
                    {row.paid ? <span className="chip ok">Paid</span> : null}
                  </td>
                  <td>
                    {open(row) ? (
                      <div className="row gap-6 nowrap">
                        {can('rooms:checkin') ? (
                          <button className="btn primary sm" onClick={() => onCheckIn(row)}>
                            Check in
                          </button>
                        ) : null}
                        {can('rooms:checkin') ? (
                          <button className="btn sm" onClick={() => setCancelling(row)}>
                            Cancel
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cancelling ? (
        <ConfirmDialog
          title={`Cancel ${cancelling.reference}?`}
          message={
            <>
              {cancelling.guestName} booked a {cancelling.roomType} for {cancelling.nights} night
              {cancelling.nights === 1 ? '' : 's'} from {cancelling.checkIn}. Cancelling puts those
              nights back on sale.
              {cancelling.paid ? ' They have already paid — refund them through your gateway.' : ''}
            </>
          }
          confirmLabel="Cancel the booking"
          danger
          busy={cancel.isPending}
          onConfirm={() => cancel.mutate(cancelling)}
          onCancel={() => setCancelling(null)}
        />
      ) : null}
    </div>
  );
}

export function FrontDeskPage() {
  const queryClient = useQueryClient();
  const { fail, push } = useToast();
  const { can } = useAuth();

  const [setupOpen, setSetupOpen] = useState(false);
  const [checkInRoom, setCheckInRoom] = useState<Room | null>(null);
  const [checkInBooking, setCheckInBooking] = useState<OnlineBooking | null>(null);
  const [statusRoom, setStatusRoom] = useState<Room | null>(null);
  const [folioStayId, setFolioStayId] = useState<string | null>(null);

  const rooms = useQuery({ queryKey: ['rooms'], queryFn: () => api.get<Room[]>('/api/rooms') });
  const summary = useQuery({
    queryKey: ['rooms', 'summary'],
    queryFn: () => api.get<RoomsSummary>('/api/rooms/summary'),
  });

  const setStatus = useMutation({
    mutationFn: (input: { roomId: string; status: string }) =>
      api.post(`/api/rooms/${input.roomId}/status`, { status: input.status }),
    onSuccess: () => {
      push('Room updated');
      setStatusRoom(null);
      void queryClient.invalidateQueries({ queryKey: ['rooms'] });
    },
    onError: fail,
  });

  if (rooms.isLoading) return <Loading label="Loading the rack…" />;
  if (rooms.error) return <ErrorState error={rooms.error} onRetry={() => void rooms.refetch()} />;

  const stats = summary.data;

  return (
    <div className="stack">
      <PageHeader
        title="Front Desk"
        subtitle="Room rack, check-in, folio and check-out"
        actions={
          can('rooms:manage') ? (
            <button className="btn" onClick={() => setSetupOpen(true)}>
              Set up rooms
            </button>
          ) : null
        }
      />

      {stats ? (
        <div className="kpis">
          <Kpi
            label="Occupancy"
            value={pct(stats.occupancyPct)}
            foot={`${stats.occupied} of ${stats.total} rooms`}
            tone="blue"
          />
          <Kpi label="ADR" value={money0(stats.adr)} foot="Average daily rate, in-house" />
          <Kpi label="RevPAR" value={money0(stats.revpar)} foot="Revenue per available room" />
          <Kpi
            label="Housekeeping"
            value={stats.dirty}
            foot={`${stats.vacant} ready · ${stats.outOfOrder} out of order`}
            tone={stats.dirty > 0 ? 'amber' : ''}
          />
        </div>
      ) : null}

      <div className="card">
        <div className="card-head">
          <h2>Room rack</h2>
          <div className="legend">
            <span>
              <i style={{ background: 'var(--ok)' }} />
              Vacant
            </span>
            <span>
              <i style={{ background: 'var(--info)' }} />
              Occupied
            </span>
            <span>
              <i style={{ background: 'var(--accent)' }} />
              Needs cleaning
            </span>
            <span>
              <i style={{ background: 'var(--danger)' }} />
              Out of order
            </span>
          </div>
        </div>
        {rooms.data!.length === 0 ? (
          <Empty
            icon="⌂"
            message="No rooms yet"
            action={
              can('rooms:manage') ? (
                <button className="btn primary" onClick={() => setSetupOpen(true)}>
                  Set up your rooms
                </button>
              ) : (
                <span className="small muted">Ask an owner or manager to add them.</span>
              )
            }
          />
        ) : (
          <div className="card-body">
            <div className="rack">
              {rooms.data!.map((room) => (
                <button
                  key={room.id}
                  className={`room ${room.status}`}
                  onClick={() => {
                    if (room.stay) setFolioStayId(room.stay.id);
                    else if (room.status === 'VACANT' && can('rooms:checkin')) setCheckInRoom(room);
                    else setStatusRoom(room);
                  }}
                >
                  <div className="row between">
                    <span className="r-no">{room.number}</span>
                    <StatusChip status={room.status} />
                  </div>
                  <div className="r-type">
                    {room.roomType} · {money0(room.tariff)}/night · GST {room.gstRate}%
                  </div>
                  <div className="r-guest">{room.stay?.guestName ?? '—'}</div>
                  <div className="r-foot">
                    <span className="xs muted">
                      {room.stay
                        ? `${room.stay.nights} night(s) · ${room.stay.pax} pax`
                        : `Sleeps ${room.maxOccupancy}`}
                    </span>
                    <span className="xs b">{room.stay ? dateShort(room.stay.checkInAt) : ''}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <OnlineBookings onCheckIn={setCheckInBooking} />

      {setupOpen ? (
        <RoomSetup
          onClose={() => {
            setSetupOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['rooms'] });
          }}
        />
      ) : null}

      {checkInRoom ? (
        <CheckInModal
          room={checkInRoom}
          onClose={() => setCheckInRoom(null)}
          onDone={(stayId) => {
            setCheckInRoom(null);
            setFolioStayId(stayId);
            void queryClient.invalidateQueries({ queryKey: ['rooms'] });
          }}
        />
      ) : null}

      {checkInBooking ? (
        <CheckInModal
          room={null}
          booking={checkInBooking}
          vacantRooms={(rooms.data ?? []).filter((r) => r.status === 'VACANT')}
          onClose={() => setCheckInBooking(null)}
          onDone={(stayId) => {
            setCheckInBooking(null);
            setFolioStayId(stayId);
            void queryClient.invalidateQueries({ queryKey: ['rooms'] });
            void queryClient.invalidateQueries({ queryKey: ['bookings'] });
          }}
        />
      ) : null}

      {statusRoom ? (
        <Modal
          title={`Room ${statusRoom.number}`}
          size="narrow"
          onClose={() => setStatusRoom(null)}
          footer={
            <>
              {statusRoom.status !== 'VACANT' ? (
                <button
                  className="btn primary"
                  onClick={() => setStatus.mutate({ roomId: statusRoom.id, status: 'VACANT' })}
                >
                  {statusRoom.status === 'DIRTY' ? 'Mark cleaned' : 'Return to service'}
                </button>
              ) : null}
              {statusRoom.status !== 'OUT_OF_ORDER' ? (
                <button
                  className="btn danger"
                  onClick={() =>
                    setStatus.mutate({ roomId: statusRoom.id, status: 'OUT_OF_ORDER' })
                  }
                >
                  Mark out of order
                </button>
              ) : null}
              <button className="btn" onClick={() => setStatusRoom(null)}>
                Close
              </button>
            </>
          }
        >
          <div className="col gap-6">
            <div className="stat-line">
              <span className="dim">Type</span>
              <b>{statusRoom.roomType}</b>
            </div>
            <div className="stat-line">
              <span className="dim">Tariff</span>
              <b>{money0(statusRoom.tariff)}</b>
            </div>
            <div className="stat-line">
              <span className="dim">Status</span>
              <StatusChip status={statusRoom.status} />
            </div>
            <div className="stat-line">
              <span className="dim">Amenities</span>
              <span className="small right">{statusRoom.amenities.join(', ')}</span>
            </div>
          </div>
        </Modal>
      ) : null}

      {folioStayId ? (
        <FolioDrawer stayId={folioStayId} onClose={() => setFolioStayId(null)} />
      ) : null}
    </div>
  );
}

/**
 * Checking somebody in, whether they walked up or booked online.
 *
 * A booking arrives with the guest, the nights and - importantly - the rate
 * they were quoted, so the desk confirms rather than re-keys, and nobody has
 * to remember what the tariff was on the day it was sold.
 */
function CheckInModal({
  room,
  booking,
  vacantRooms,
  onClose,
  onDone,
}: {
  room: Room | null;
  booking?: OnlineBooking | null;
  vacantRooms?: Room[];
  onClose: () => void;
  onDone: (stayId: string) => void;
}) {
  const { fail, push } = useToast();

  /* Rooms of the type booked come first; any other vacant room is an upgrade
     the desk is free to make. */
  const choices = (vacantRooms ?? []).slice().sort((a, b) => {
    const wanted = booking?.roomTypeId;
    const rank = (r: Room) => (wanted && r.roomTypeId === wanted ? 0 : 1);
    return rank(a) - rank(b) || a.number.localeCompare(b.number);
  });

  const [roomId, setRoomId] = useState(room?.id ?? choices[0]?.id ?? '');
  const chosen = room ?? choices.find((r) => r.id === roomId) ?? null;

  const [form, setForm] = useState({
    name: booking?.guestName ?? '',
    phone: booking?.guestPhone ?? '',
    email: booking?.guestEmail ?? '',
    idType: 'AADHAAR',
    idNumber: '',
    gstin: '',
    nights: booking?.nights ?? 1,
    adults: booking?.adults ?? 2,
    children: booking?.children ?? 0,
    tariff: booking?.tariff ?? room?.tariff ?? 0,
    advance: 0,
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const checkIn = useMutation({
    mutationFn: () =>
      api.post<Folio>(`/api/rooms/${roomId}/check-in`, {
        guest: {
          name: form.name,
          phone: form.phone,
          email: form.email,
          idType: form.idType,
          idNumber: form.idNumber,
          gstin: form.gstin,
        },
        nights: form.nights,
        adults: form.adults,
        children: form.children,
        tariff: form.tariff,
        advance: form.advance,
        ...(booking ? { bookingId: booking.id } : {}),
      }),
    onSuccess: (folio) => {
      push(`${form.name} checked into ${folio.stay.roomNumber} · ${folio.stay.folioNo}`, 'ok');
      onDone(folio.stay.id);
    },
    onError: fail,
  });

  const fieldErrors =
    checkIn.error instanceof Error && 'fieldErrors' in checkIn.error
      ? (checkIn.error as { fieldErrors: Record<string, string> }).fieldErrors
      : {};

  return (
    <Modal
      title={
        booking ? `Check in · ${booking.reference}` : `Check in · Room ${chosen?.number ?? ''}`
      }
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!form.name.trim() || !roomId || checkIn.isPending}
            onClick={() => checkIn.mutate()}
          >
            {checkIn.isPending ? 'Checking in…' : 'Check in'}
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <div className="row gap-6 wrap">
          {chosen ? (
            <>
              <span className="chip brand">{chosen.roomType}</span>
              <span className="chip">{money0(form.tariff)} / night</span>
              <span className="chip">Sleeps {chosen.maxOccupancy}</span>
              <span className="chip info">GST {chosen.gstRate}%</span>
            </>
          ) : null}
          {booking ? <span className="chip ok">Booked online</span> : null}
          {booking?.paid ? <span className="chip ok">Paid</span> : null}
        </div>

        {booking ? (
          <p className="small muted">
            {booking.guestName} booked a {booking.roomType} for {booking.nights} night
            {booking.nights === 1 ? '' : 's'} from {booking.checkIn} at {money0(booking.tariff)} a
            night. The rate they were quoted is what they are charged.
          </p>
        ) : null}

        <div className="form-grid">
          {booking ? (
            <Select
              className="span2"
              label="Room *"
              hint={
                choices.length === 0
                  ? 'No room is free — clean one, or free one up first'
                  : `Rooms of the booked type are listed first`
              }
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
            >
              {choices.length === 0 ? <option value="">Nothing vacant</option> : null}
              {choices.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.number} · {option.roomType}
                  {option.roomTypeId === booking.roomTypeId ? '' : ' (different type)'}
                </option>
              ))}
            </Select>
          ) : null}
          <TextInput
            className="span2"
            label="Guest name *"
            autoFocus
            value={form.name}
            error={fieldErrors['guest.name']}
            onChange={(e) => set('name', e.target.value)}
            placeholder="As printed on the ID"
          />
          <TextInput
            label="Phone"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            placeholder="+91 …"
          />
          <TextInput
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
          />
          <Select
            label="ID type"
            value={form.idType}
            onChange={(e) => set('idType', e.target.value)}
          >
            <option value="AADHAAR">Aadhaar</option>
            <option value="PASSPORT">Passport</option>
            <option value="DRIVING_LICENCE">Driving licence</option>
            <option value="VOTER_ID">Voter ID</option>
          </Select>
          <TextInput
            label="ID number"
            value={form.idNumber}
            onChange={(e) => set('idNumber', e.target.value)}
          />
          <TextInput
            className="span2"
            label="Company GSTIN (optional)"
            value={form.gstin}
            error={fieldErrors['guest.gstin']}
            hint="A GSTIN from another state switches the folio to IGST"
            onChange={(e) => set('gstin', e.target.value.toUpperCase())}
          />
        </div>

        <div className="form-grid three">
          <TextInput
            label="Nights"
            type="number"
            min={1}
            value={form.nights}
            onChange={(e) => set('nights', Number(e.target.value) || 1)}
          />
          <TextInput
            label="Adults"
            type="number"
            min={1}
            value={form.adults}
            onChange={(e) => set('adults', Number(e.target.value) || 1)}
          />
          <TextInput
            label="Children"
            type="number"
            min={0}
            value={form.children}
            onChange={(e) => set('children', Number(e.target.value) || 0)}
          />
          <TextInput
            label="Tariff / night"
            type="number"
            value={form.tariff}
            onChange={(e) => set('tariff', Number(e.target.value) || 0)}
          />
          <TextInput
            label="Advance collected"
            type="number"
            value={form.advance}
            onChange={(e) => set('advance', Number(e.target.value) || 0)}
          />
        </div>
      </div>
    </Modal>
  );
}

function FolioDrawer({ stayId, onClose }: { stayId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { fail, push } = useToast();
  const { can } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [receipt, setReceipt] = useState<Invoice | null>(null);
  const [amend, setAmend] = useState<{ nights: number; tariff: number; advance: number } | null>(
    null,
  );

  const folio = useQuery({
    queryKey: ['folio', stayId],
    queryFn: () => api.get<Folio>(`/api/stays/${stayId}/folio`),
  });

  const update = useMutation({
    mutationFn: (input: { nights: number; tariff: number; advance: number }) =>
      api.patch<Folio>(`/api/stays/${stayId}`, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['folio', stayId], data);
      setAmend(null);
      push('Folio updated');
    },
    onError: fail,
  });

  const checkOut = useMutation({
    mutationFn: () =>
      api.post<{ invoice: Invoice; balanceSettled: number }>(`/api/stays/${stayId}/check-out`, {
        mode: 'CARD',
      }),
    onSuccess: (result) => {
      push(`Checked out · ${result.invoice.number}`, 'ok');
      setConfirming(false);
      setReceipt(result.invoice);
      void queryClient.invalidateQueries({ queryKey: ['rooms'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      setConfirming(false);
      fail(error);
    },
  });

  const openRoomService = useMutation({
    mutationFn: () => api.post('/api/tickets', { type: 'ROOM_SERVICE', stayId, covers: 1 }),
    onSuccess: () => {
      push('Room-service ticket opened');
      navigate('/pos');
    },
    onError: fail,
  });

  const data = folio.data;

  return (
    <>
      <Drawer
        title={data ? `Folio ${data.stay.folioNo} · Room ${data.stay.roomNumber}` : 'Folio'}
        size="wide"
        onClose={onClose}
        footer={
          data && data.stay.status === 'IN_HOUSE' ? (
            <>
              <button className="btn" onClick={onClose}>
                Close
              </button>
              <button className="btn" onClick={() => openRoomService.mutate()}>
                Room-service ticket
              </button>
              {can('rooms:checkout') ? (
                <button className="btn primary" onClick={() => setConfirming(true)}>
                  Check out &amp; settle
                </button>
              ) : null}
            </>
          ) : (
            <button className="btn" onClick={onClose}>
              Close
            </button>
          )
        }
      >
        {folio.isLoading || !data ? (
          <Loading />
        ) : (
          <div className="stack">
            <div className="card">
              <div className="card-body row between wrap gap-16">
                <div>
                  <h2>{data.stay.guestName}</h2>
                  <div className="small muted">
                    {data.stay.phone ?? 'No phone'}{' '}
                    {data.stay.gstin ? `· GSTIN ${data.stay.gstin}` : ''}
                  </div>
                  <div className="small dim" style={{ marginTop: 4 }}>
                    Checked in {dateTime(data.stay.checkInAt)} · {data.stay.nightsCharged} night
                    {data.stay.nightsCharged === 1 ? '' : 's'} · {data.stay.adults +
                      data.stay.children}{' '}
                    pax
                  </div>
                  {data.stay.nightsCharged > data.stay.nights ? (
                    /* Said one night on arrival, still here on the third: the
                       desk should see that the bill has followed the room. */
                    <div className="small warn" style={{ marginTop: 4 }}>
                      Booked {data.stay.nights} night{data.stay.nights === 1 ? '' : 's'}, staying{' '}
                      {data.stay.nightsCharged}. The folio charges the nights actually taken.
                    </div>
                  ) : null}
                </div>
                <div className="right">
                  <div className="muted xs">Balance due</div>
                  <div style={{ fontSize: 24, fontWeight: 750 }}>{money(data.balance)}</div>
                  <div className="xs dim">Advance {money(data.advance)}</div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h2>Folio charges</h2>
                <span className="chip">{data.lines.length} line(s)</span>
              </div>
              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>HSN</th>
                      <th className="num">Qty</th>
                      <th className="num">Rate</th>
                      <th className="num">Taxable</th>
                      <th className="num">GST</th>
                      <th className="num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lines.map((line, index) => (
                      <tr key={`${line.description}-${index}`}>
                        <td>
                          {line.description}
                          {line.sourceInvoiceNumber ? (
                            <div className="xs muted mono">{line.sourceInvoiceNumber}</div>
                          ) : null}
                        </td>
                        <td className="mono xs">{line.hsn}</td>
                        <td className="num">{line.quantity}</td>
                        <td className="num">{money(line.unitPrice)}</td>
                        <td className="num">{money(line.taxable)}</td>
                        <td className="num dim">
                          {line.gstRate}% · {money(line.cgst + line.sgst + line.igst)}
                        </td>
                        <td className="num b">{money(line.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={6}>Taxable value</td>
                      <td className="num">{money(data.totals.subtotal)}</td>
                    </tr>
                    <tr>
                      <td colSpan={6}>GST</td>
                      <td className="num">
                        {money(data.totals.cgst + data.totals.sgst + data.totals.igst)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={6}>Folio total</td>
                      <td className="num">{money(data.totals.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {data.stay.status === 'IN_HOUSE' && can('rooms:checkin') ? (
              <div className="card">
                <div className="card-head">
                  <h2>Amend stay</h2>
                  {!amend ? (
                    <button
                      className="btn sm"
                      onClick={() =>
                        setAmend({
                          nights: data.stay.nights,
                          tariff: data.stay.tariff,
                          advance: data.advance,
                        })
                      }
                    >
                      Edit
                    </button>
                  ) : null}
                </div>
                {amend ? (
                  <div className="card-body">
                    <div className="form-grid three">
                      <TextInput
                        label="Nights"
                        type="number"
                        min={1}
                        value={amend.nights}
                        onChange={(e) =>
                          setAmend({ ...amend, nights: Number(e.target.value) || 1 })
                        }
                      />
                      <TextInput
                        label="Tariff"
                        type="number"
                        value={amend.tariff}
                        onChange={(e) =>
                          setAmend({ ...amend, tariff: Number(e.target.value) || 0 })
                        }
                      />
                      <TextInput
                        label="Advance"
                        type="number"
                        value={amend.advance}
                        onChange={(e) =>
                          setAmend({ ...amend, advance: Number(e.target.value) || 0 })
                        }
                      />
                    </div>
                    <div className="row gap-6" style={{ marginTop: 11 }}>
                      <button
                        className="btn primary sm"
                        onClick={() => update.mutate(amend)}
                        disabled={update.isPending}
                      >
                        Save changes
                      </button>
                      <button className="btn sm" onClick={() => setAmend(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </Drawer>

      {confirming && data ? (
        <ConfirmDialog
          title="Check out"
          message={`Settle ${money(data.balance)} for ${data.stay.guestName} and release room ${data.stay.roomNumber}?`}
          confirmLabel="Check out"
          busy={checkOut.isPending}
          onConfirm={() => checkOut.mutate()}
          onCancel={() => setConfirming(false)}
        />
      ) : null}

      {receipt ? (
        <Modal
          title={`Invoice ${receipt.number}`}
          size="wide"
          onClose={() => {
            setReceipt(null);
            onClose();
          }}
          footer={
            <>
              <button
                className="btn"
                onClick={() => {
                  setReceipt(null);
                  onClose();
                }}
              >
                Close
              </button>
              <button className="btn primary" onClick={() => window.print()}>
                Print
              </button>
            </>
          }
        >
          <InvoiceDoc invoice={receipt} />
        </Modal>
      ) : null}
    </>
  );
}
